const initializeFilazooMenuIndicators = (root = document) => {
  root.querySelectorAll('.header__inline-menu').forEach((menu) => {
    if (menu.dataset.filazooIndicatorReady === 'true') return;

    const items = menu.querySelectorAll(
      ':scope > .list-menu--inline > li > a.header__menu-item:not([data-filazoo-nav-accent]), :scope > .list-menu--inline > li > header-menu > details > summary.header__menu-item'
    );

    // Items with their own animation (the wholesale pill) opt out of the shared
    // bar, and hovering one parks it so a stale bar is not left behind.
    const accentItems = menu.querySelectorAll(
      ':scope > .list-menu--inline > li > [data-filazoo-nav-accent]'
    );

    if (!items.length) return;

    let pointerInside = false;
    let focusInside = false;

    const moveIndicator = (item) => {
      const menuRect = menu.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const labelRect = item.querySelector(':scope > span:not(.visually-hidden)')?.getBoundingClientRect() ?? itemRect;
      const isVisible = menu.classList.contains('filazoo-menu-indicator--visible');

      if (!isVisible) menu.classList.add('filazoo-menu-indicator--placing');

      menu.style.setProperty('--filazoo-menu-indicator-x', `${labelRect.left - menuRect.left}px`);
      menu.style.setProperty('--filazoo-menu-indicator-width', `${labelRect.width}px`);

      if (isVisible) return;

      menu.getBoundingClientRect();
      menu.classList.remove('filazoo-menu-indicator--placing');
      menu.classList.add('filazoo-menu-indicator--visible');
    };

    const hideIndicator = () => {
      if (!pointerInside && !focusInside) {
        menu.classList.remove('filazoo-menu-indicator--visible');
      }
    };

    items.forEach((item) => {
      item.addEventListener('pointerenter', () => moveIndicator(item));
      item.addEventListener('focus', () => moveIndicator(item));
    });

    accentItems.forEach((item) => {
      const parkIndicator = () => menu.classList.remove('filazoo-menu-indicator--visible');
      item.addEventListener('pointerenter', parkIndicator);
      item.addEventListener('focus', parkIndicator);
    });

    menu.addEventListener('pointerenter', () => {
      pointerInside = true;
    });

    menu.addEventListener('pointerleave', () => {
      pointerInside = false;
      hideIndicator();
    });

    menu.addEventListener('focusin', () => {
      focusInside = true;
    });

    menu.addEventListener('focusout', () => {
      requestAnimationFrame(() => {
        focusInside = menu.contains(document.activeElement);
        hideIndicator();
      });
    });

    menu.dataset.filazooIndicatorReady = 'true';
  });
};

const syncFilazooMegaMenuBackdrop = () => {
  const isHomepage = document.querySelector('#MainContent[data-template="index"]');
  const hasOpenMegaMenu = document.querySelector('.section-header .mega-menu--style-1[open]');

  document.body.classList.toggle('filazoo-mega-menu-open', Boolean(hasOpenMegaMenu));
  document.body.classList.toggle(
    'filazoo-home-mega-menu-open',
    Boolean(isHomepage && hasOpenMegaMenu)
  );
};

const initializeFilazooMegaMenus = (root = document) => {
  root.querySelectorAll('.mega-menu--style-1').forEach((menu) => {
    if (menu.dataset.filazooMegaReady === 'true') return;

    let closeTimer;
    const desktopQuery = window.matchMedia('(min-width: 1200px)');
    const tabs = Array.from(menu.querySelectorAll('[data-mega-menu-tab]'));
    const panels = Array.from(menu.querySelectorAll('[data-mega-menu-panel]'));
    const content = menu.querySelector(':scope > .mega-menu__content');

    const replayOpenAnimation = () => {
      if (!content || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      content.classList.remove('filazoo-mega-menu--animating');
      content.getBoundingClientRect();
      content.classList.add('filazoo-mega-menu--animating');
    };

    const activatePanel = (tab) => {
      const targetId = tab?.dataset.panelTarget;
      if (!targetId) return;

      tabs.forEach((item) => {
        const isSelected = item === tab;
        item.classList.toggle('is-selected', isSelected);
        item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      });

      panels.forEach((panel) => {
        const isSelected = panel.id === targetId;
        panel.hidden = !isSelected;
        panel.classList.toggle('is-selected', isSelected);
      });
    };

    const openMenu = (replayAnimation = false) => {
      if (!desktopQuery.matches) return;
      window.clearTimeout(closeTimer);
      menu.closest('.header__inline-menu')
        ?.querySelectorAll('.mega-menu[open]')
        .forEach((openMenuElement) => {
          if (openMenuElement !== menu) openMenuElement.removeAttribute('open');
        });
      menu.setAttribute('open', '');
      menu.querySelector('summary')?.setAttribute('aria-expanded', 'true');
      if (replayAnimation) replayOpenAnimation();
    };

    const scheduleClose = () => {
      if (!desktopQuery.matches) return;
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        if (!menu.matches(':hover') && !menu.contains(document.activeElement)) {
          menu.removeAttribute('open');
          menu.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        }
      }, 140);
    };

    menu.addEventListener('pointerenter', () => openMenu(true));
    menu.addEventListener('pointerleave', scheduleClose);
    menu.addEventListener('focusin', () => openMenu(!menu.hasAttribute('open')));
    menu.addEventListener('focusout', scheduleClose);

    tabs.forEach((tab) => {
      tab.addEventListener('pointerenter', () => activatePanel(tab));
      tab.addEventListener('focus', () => activatePanel(tab));
      tab.addEventListener('click', (event) => {
        activatePanel(tab);
        if (tab.getAttribute('href') === '#') event.preventDefault();
      });
    });

    menu.activateFilazooPanel = activatePanel;
    new MutationObserver(syncFilazooMegaMenuBackdrop).observe(menu, {
      attributes: true,
      attributeFilter: ['open'],
    });
    menu.dataset.filazooMegaReady = 'true';
  });

  syncFilazooMegaMenuBackdrop();
};

const initializeFilazooDropdownMenus = (root = document) => {
  root.querySelectorAll('.filazoo-dropdown').forEach((dropdown) => {
    if (dropdown.dataset.filazooDropdownReady === 'true') return;

    const summary = dropdown.querySelector(':scope > summary');
    const desktopQuery = window.matchMedia('(min-width: 1200px)');
    const hoverQuery = window.matchMedia('(min-width: 1200px) and (hover: hover)');
    let closeTimer;

    const openDropdown = () => {
      if (!desktopQuery.matches) return;
      window.clearTimeout(closeTimer);

      dropdown.closest('.header__inline-menu')
        ?.querySelectorAll('.filazoo-dropdown[open]')
        .forEach((openDropdownElement) => {
          if (openDropdownElement === dropdown) return;
          openDropdownElement.removeAttribute('open');
          openDropdownElement.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
        });

      dropdown.setAttribute('open', '');
      summary?.setAttribute('aria-expanded', 'true');
    };

    const scheduleClose = () => {
      if (!desktopQuery.matches) return;
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        if (!dropdown.matches(':hover') && !dropdown.contains(document.activeElement)) {
          dropdown.removeAttribute('open');
          summary?.setAttribute('aria-expanded', 'false');
        }
      }, 140);
    };

    dropdown.addEventListener('pointerenter', () => {
      if (hoverQuery.matches) openDropdown();
    });
    dropdown.addEventListener('pointerleave', () => {
      if (hoverQuery.matches) scheduleClose();
    });
    dropdown.addEventListener('focusin', openDropdown);
    dropdown.addEventListener('focusout', scheduleClose);
    summary?.addEventListener('click', (event) => {
      if (hoverQuery.matches && event.detail > 0) event.preventDefault();
    });

    dropdown.dataset.filazooDropdownReady = 'true';
  });
};

const initializeFilazooMobileMegaMenus = (root = document) => {
  root.querySelectorAll('[data-mobile-mega-menu]').forEach((menu) => {
    if (menu.dataset.filazooMobileMegaReady === 'true') return;

    const tabs = Array.from(menu.querySelectorAll('[data-mobile-mega-tab]'));
    const panels = Array.from(menu.querySelectorAll('[data-mobile-mega-panel]'));

    const activatePanel = (tab, moveFocus = false) => {
      const targetId = tab?.dataset.panelTarget;
      if (!targetId) return;

      tabs.forEach((item) => {
        const isSelected = item === tab;
        item.classList.toggle('is-selected', isSelected);
        item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        item.tabIndex = isSelected ? 0 : -1;
      });

      panels.forEach((panel) => {
        const isSelected = panel.id === targetId;
        panel.hidden = !isSelected;
        panel.classList.toggle('is-selected', isSelected);
        if (isSelected) panel.scrollTop = 0;
      });

      if (moveFocus) tab.focus();
    };

    tabs.forEach((tab, index) => {
      tab.tabIndex = tab.classList.contains('is-selected') ? 0 : -1;
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        activatePanel(tab);
      });
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();

        let nextIndex = index;
        if (event.key === 'ArrowDown') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowUp') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        activatePanel(tabs[nextIndex], true);
      });
    });

    menu.dataset.filazooMobileMegaReady = 'true';
  });
};

const initializeFilazooDrawerChrome = (root = document) => {
  root.querySelectorAll('header-drawer').forEach((drawer) => {
    const closeButton = drawer.querySelector('[data-menu-drawer-close]');
    if (!closeButton || closeButton.dataset.filazooDrawerCloseReady === 'true') return;

    closeButton.addEventListener('click', () => {
      const mainDetails = drawer.querySelector(':scope > details');
      if (!mainDetails?.open) return;
      mainDetails.querySelector(':scope > summary')?.click();
    });

    closeButton.dataset.filazooDrawerCloseReady = 'true';
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeFilazooMenuIndicators();
    initializeFilazooMegaMenus();
    initializeFilazooDropdownMenus();
    initializeFilazooMobileMegaMenus();
    initializeFilazooDrawerChrome();
  });
} else {
  initializeFilazooMenuIndicators();
  initializeFilazooMegaMenus();
  initializeFilazooDropdownMenus();
  initializeFilazooMobileMegaMenus();
  initializeFilazooDrawerChrome();
}

document.addEventListener('shopify:section:load', (event) => {
  initializeFilazooMenuIndicators(event.target);
  initializeFilazooMegaMenus(event.target);
  initializeFilazooDropdownMenus(event.target);
  initializeFilazooMobileMegaMenus(event.target);
  initializeFilazooDrawerChrome(event.target);
});

document.addEventListener('shopify:block:select', (event) => {
  const menu = event.target.matches('.mega-menu--style-1')
    ? event.target
    : event.target.closest('.mega-menu--style-1');

  if (!menu) return;
  menu.setAttribute('open', '');
  menu.querySelector('summary')?.setAttribute('aria-expanded', 'true');
  const selectedTab = event.target.matches('[data-mega-menu-tab]')
    ? event.target
    : event.target.querySelector('[data-mega-menu-tab]');
  if (selectedTab) menu.activateFilazooPanel?.(selectedTab);
});

document.addEventListener('shopify:block:deselect', (event) => {
  const menu = event.target.matches('.mega-menu--style-1')
    ? event.target
    : event.target.closest('.mega-menu--style-1');

  if (!menu || menu.matches(':hover') || menu.contains(document.activeElement)) return;
  menu.removeAttribute('open');
  menu.querySelector('summary')?.setAttribute('aria-expanded', 'false');
});
